import React from 'react';

/**
 * 找出「校验拦住了保存，但页面上没有对应输入框」的字段。
 *
 * 为什么需要：校验规则和渲染分支是各写各的，没有任何机制保证一致。本仓库已经出过三次
 * 同一类问题 —— 服务级 sandboxConfig（端点模型迁移后顶层副本没有 UI）、rentalConfig.rentalKey
 * （只在 TIME_RENTAL 分支渲染，却对所有端点校验）、Sandbox 价格输入框（漏传 error prop）。
 * 表现完全一样：保存按钮变灰、提示「有 N 个字段需要修正」，但页面上一个红字都没有，
 * 人只能干瞪眼。
 *
 * 判定方式：react-hook-form 注册的输入框带 name 属性且等于错误路径，DOM 里查不到就是够不着。
 * 这是兜底，不是修复 —— 出现在这个清单里的字段都该被当成 bug 修掉。
 */
export const collectUnreachableErrors = (errors) => {
  const found = [];
  const walk = (node, path) => {
    if (!node || typeof node !== 'object') return;
    // react-hook-form 的叶子错误形如 { type, message, ref }
    if (typeof node.message === 'string' && node.message) {
      found.push({ path, message: node.message });
      return;
    }
    Object.keys(node).forEach((key) => {
      if (key === 'ref' || key === 'type' || key === 'types') return;
      walk(node[key], path ? `${path}.${key}` : key);
    });
  };
  walk(errors, '');

  return found.filter(({ path }) => {
    if (!path) return false;
    // 名字完全匹配，或错误挂在对象上而输入框在其子字段（如 pricing → pricing.runningPerHour）
    const escaped = path.replace(/"/g, '\\"');
    try {
      return !document.querySelector(
        `[name="${escaped}"], [name^="${escaped}."]`,
      );
    } catch {
      return false;
    }
  });
};

/**
 * 兜底清单。只在真的有「够不着」的错误时出现 —— 平时应该始终为空。
 */
export const UnreachableErrorList = ({ errors }) => {
  const items = React.useMemo(() => collectUnreachableErrors(errors), [errors]);
  if (!items.length) return null;
  return (
    <div
      className="card"
      style={{
        padding: '12px 14px',
        marginTop: 12,
        borderColor: 'var(--danger, #b91c1c)',
        fontSize: 13,
        lineHeight: 1.7,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        以下问题挡住了保存，但在当前页面上没有对应的输入框：
      </div>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {items.map(({ path, message }) => (
          <li key={path}>
            <code style={{ marginRight: 6 }}>{path}</code>
            {message}
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 8, color: 'var(--fg-3)' }}>
        这属于平台的配置缺陷，请把上面这段截图反馈给我们；同一份配置换个入口保存通常可以绕过。
      </div>
    </div>
  );
};
